// Couche de données hybride :
//  - si Supabase est configuré (.env.local) → vraie base de données partagée
//  - sinon → localStorage (mode développement local)
// Toutes les fonctions sont asynchrones pour que les écrans n'aient
// jamais à changer selon le mode.

import { supabase } from './supabaseClient';
import { WORKS, coverUrl } from './worksData';

/* ---------- Fallback localStorage ---------- */

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const write = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const makeId = () =>
  Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7);

/* ---------- Messages ---------- */

export async function getMessages() {
  if (supabase) {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .order('date', { ascending: true });
    if (error) {
      console.error('getMessages:', error.message);
      return [];
    }
    return data;
  }
  return read('oblik_messages', []);
}

export async function addMessage({ autor, mail, content }) {
  const item = {
    autor,
    mail,
    content,
    reply: '',
    status: 'pending',
    date: new Date().toISOString(),
  };
  if (supabase) {
    // Insertion SANS relecture : un visiteur anonyme n'a le droit de lire que
    // les messages publiés, pas le sien qui est "en attente". Demander le
    // retour de la ligne (.select()) faisait donc échouer tout l'envoi.
    const { error } = await supabase.from('messages').insert(item);
    if (error) {
      console.error('addMessage:', error.message);
      return null;
    }
    return item;
  }
  const list = read('oblik_messages', []);
  const withId = { id: makeId(), ...item };
  list.push(withId);
  write('oblik_messages', list);
  return withId;
}

export async function updateMessage(id, patch) {
  if (supabase) {
    const { error } = await supabase.from('messages').update(patch).eq('id', id);
    if (error) console.error('updateMessage:', error.message);
    return getMessages();
  }
  const list = read('oblik_messages', []).map((m) =>
    m.id === id ? { ...m, ...patch } : m
  );
  write('oblik_messages', list);
  return list;
}

/* ---------- Catégories ---------- */

const DEFAULT_CATEGORIES = ['Conception graphique', 'Production', 'Stratégie'];

export async function getCategories() {
  if (supabase) {
    const { data, error } = await supabase
      .from('categories')
      .select('name')
      .order('name');
    if (error) {
      console.error('getCategories:', error.message);
      return DEFAULT_CATEGORIES;
    }
    return data.map((c) => c.name);
  }
  return read('oblik_categories', DEFAULT_CATEGORIES);
}

export async function addCategory(name) {
  const clean = name.trim();
  if (!clean) return getCategories();
  if (supabase) {
    const { error } = await supabase.from('categories').insert({ name: clean });
    if (error && error.code !== '23505') console.error('addCategory:', error.message);
    return getCategories();
  }
  const list = read('oblik_categories', DEFAULT_CATEGORIES);
  if (!list.includes(clean)) {
    list.push(clean);
    write('oblik_categories', list);
  }
  return list;
}

/* ---------- Travaux ---------- */

export async function getWorks() {
  if (supabase) {
    const { data, error } = await supabase
      .from('works')
      .select('*')
      .order('date', { ascending: true });
    if (error) {
      console.error('getWorks:', error.message);
      return [];
    }
    return data;
  }
  return read('oblik_works', []);
}

// Prépare la liste finale des visuels, dans l'ordre : les nouveaux fichiers
// sont envoyés au stockage, ceux déjà en ligne sont conservés tels quels.
// Un envoi raté interrompt tout — mieux vaut le signaler que d'enregistrer
// un travail incomplet sans que personne ne le sache.
async function preparerImages(media) {
  const images = [];
  for (const m of media) {
    if (!m.file) {
      // visuel déjà présent : on garde son adresse
      if (m.url) images.push({ url: m.url, role: m.role, cover: !!m.cover });
      continue;
    }
    const path = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}_${m.file.name}`;
    const { error: upErr } = await supabase.storage
      .from('works')
      .upload(path, m.file, { contentType: m.file.type || undefined });
    if (upErr) {
      console.error('upload media:', upErr.message);
      const taille = Math.round(m.file.size / 1048576);
      return {
        error: `Envoi de « ${m.file.name} » (${taille} Mo) impossible : ${upErr.message}`,
      };
    }
    const { data } = supabase.storage.from('works').getPublicUrl(path);
    images.push({ url: data.publicUrl, role: m.role, cover: !!m.cover });
  }
  return { images };
}

// `media` : tableau ordonné de { file, preview, role } (nouveaux visuels)
// ou { url, role } (visuels déjà en ligne)
export async function addWork({
  title, subtitle, description, challenge = '', solution = '',
  category, media = [],
}) {
  if (supabase) {
    const prepa = await preparerImages(media);
    if (prepa.error) return prepa;
    const images = prepa.images;
    const { error } = await supabase.from('works').insert({
      title,
      subtitle,
      description,
      challenge,
      solution,
      category,
      images,
      date: new Date().toISOString(),
    });
    if (error) {
      console.error('addWork:', error.message);
      return { error: `Enregistrement impossible : ${error.message}` };
    }
    return { ok: true };
  }
  const list = read('oblik_works', []);
  const item = {
    id: makeId(),
    title,
    subtitle,
    description,
    challenge,
    solution,
    category,
    images: media.map((m) => ({ url: m.url || m.preview, role: m.role, cover: !!m.cover })),
    date: new Date().toISOString(),
  };
  list.push(item);
  write('oblik_works', list);
  return { ok: true };
}

// Modifie un travail existant : mêmes champs qu'à la création, et la liste
// complète des visuels (ceux conservés + les nouveaux, dans l'ordre voulu).
export async function updateWork(id, {
  title, subtitle, description, challenge = '', solution = '',
  category, media = [],
}) {
  if (supabase) {
    const prepa = await preparerImages(media);
    if (prepa.error) return prepa;
    const { error } = await supabase
      .from('works')
      .update({
        title, subtitle, description, challenge, solution,
        category, images: prepa.images,
      })
      .eq('id', id);
    if (error) {
      console.error('updateWork:', error.message);
      return { error: `Enregistrement impossible : ${error.message}` };
    }
    return { ok: true };
  }
  const list = read('oblik_works', []).map((w) =>
    w.id === id
      ? {
          ...w,
          title, subtitle, description, challenge, solution, category,
          images: media.map((m) => ({ url: m.url || m.preview, role: m.role, cover: !!m.cover })),
        }
      : w
  );
  write('oblik_works', list);
  return { ok: true };
}

/* ---------- Visuels mis en avant (démarrage du site + fond d'accueil) ---------- */

const HIGHLIGHTS_KEY = 'oblik_highlights';

// Liste d'URLs choisies dans l'admin. Vide = on affiche tous les travaux.
export async function getHighlights() {
  if (supabase) {
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'home_highlights')
      .maybeSingle();
    if (error) {
      // Table absente = SQL pas encore exécuté : on retombe simplement sur
      // "tous les travaux", sans bruit dans la console.
      const msg = error.message || '';
      const tableManquante =
        error.code === 'PGRST205' ||
        error.code === '42P01' ||
        /could not find the table|does not exist/i.test(msg);
      if (!tableManquante) console.error('getHighlights:', msg);
      return [];
    }
    return (data && data.value) || [];
  }
  return read(HIGHLIGHTS_KEY, []);
}

// Renvoie { ok: true } ou { ok: false, reason } — l'appelant doit prévenir
// l'utilisateur en cas d'échec plutôt qu'afficher une fausse confirmation.
export async function setHighlights(urls) {
  if (supabase) {
    const { error } = await supabase
      .from('settings')
      .upsert({ key: 'home_highlights', value: urls }, { onConflict: 'key' });
    if (error) {
      const msg = error.message || '';
      const tableManquante =
        error.code === 'PGRST205' ||
        error.code === '42P01' ||
        /could not find the table|does not exist/i.test(msg);
      return {
        ok: false,
        reason: tableManquante ? 'table-manquante' : msg,
      };
    }
    return { ok: true };
  }
  write(HIGHLIGHTS_KEY, urls);
  return { ok: true };
}

/* ---------- Travaux de démonstration masqués ---------- */
// Les 14 travaux d'exemple vivent dans le code (worksData.js) : on ne peut
// pas les supprimer de la base. On mémorise donc ceux que l'admin a retirés
// de l'affichage, par leur nom.

const HIDDEN_KEY = 'oblik_hidden_works';
const DELETED_KEY = 'oblik_deleted_works';

// Lecture générique d'un réglage (tableau) — tolère l'absence de la table.
async function lireReglage(cle, cleLocale) {
  if (supabase) {
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', cle)
      .maybeSingle();
    if (error) {
      const msg = error.message || '';
      const tableManquante =
        error.code === 'PGRST205' || error.code === '42P01' ||
        /could not find the table|does not exist/i.test(msg);
      if (!tableManquante) console.error(cle + ':', msg);
      return [];
    }
    return (data && data.value) || [];
  }
  return read(cleLocale, []);
}

async function ecrireReglage(cle, cleLocale, valeur) {
  if (supabase) {
    const { error } = await supabase
      .from('settings')
      .upsert({ key: cle, value: valeur }, { onConflict: 'key' });
    if (error) return { ok: false, reason: error.message };
    return { ok: true };
  }
  write(cleLocale, valeur);
  return { ok: true };
}

// Travaux d'exemple définitivement écartés : ils ne réapparaissent plus,
// ni sur le site, ni dans la liste de l'admin.
export const getDeletedWorks = () => lireReglage('deleted_works', DELETED_KEY);
export const setDeletedWorks = (labels) =>
  ecrireReglage('deleted_works', DELETED_KEY, labels);

export const getHiddenWorks = () => lireReglage('hidden_works', HIDDEN_KEY);
export const setHiddenWorks = (labels) =>
  ecrireReglage('hidden_works', HIDDEN_KEY, labels);

// Liste des travaux telle qu'elle doit s'afficher sur le site : les exemples
// non masqués, suivis de ceux ajoutés depuis l'admin. Utilisée par toutes les
// pages pour qu'un travail retiré disparaisse partout d'un coup.
export async function loadDisplayWorks() {
  const [admin, masques, supprimes] = await Promise.all([
    getWorks(), getHiddenWorks(), getDeletedWorks(),
  ]);
  const demos = WORKS.filter(
    (w) => !masques.includes(w.label) && !supprimes.includes(w.label)
  );
  const ajoutes = (admin || [])
    .filter((w) => w.title)
    .map((w) => ({
      label: w.title,
      link: '#',
      image: coverUrl(w),
      cat: '',
      subtitle: w.subtitle || '',
      description: w.description || '',
      challenge: w.challenge || '',
      solution: w.solution || '',
      images: w.images || [],
      category: w.category || '',
    }));
  return [...demos, ...ajoutes];
}

export async function deleteWork(id) {
  if (supabase) {
    const { error } = await supabase.from('works').delete().eq('id', id);
    if (error) console.error('deleteWork:', error.message);
    return getWorks();
  }
  const list = read('oblik_works', []).filter((w) => w.id !== id);
  write('oblik_works', list);
  return list;
}
